import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup
} from "react-bootstrap";

const BASE =
    process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";

const API_URL = `${BASE}/api/news`;
const IMAGE_URL = `${BASE}/uploads/`;

const Business = () => {
    const [businessNews, setBusinessNews] = useState([]);
    const [show, setShow] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);

    useEffect(() => {
        fetchBusinessNews();
    }, []);

    const getImage = (item) => {
        if (item?.imagePath?.startsWith("http")) return item.imagePath;
        if (item?.image) return `${IMAGE_URL}${item.image}`;
        return "https://via.placeholder.com/400x250?text=No+Image";
    };

    const getViews = (item) => {
        const v =
            item?.views ??
            item?.viewCount ??
            item?.totalViews ??
            item?.impressions ??
            0;

        return Number(v) || 0;
    };

    const formatViews = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n;
    };

    const fetchBusinessNews = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const data = result?.data || [];

            const business = data.filter(item =>
                ["business", "stock", "market", "startup", "economy"].includes(
                    item.category?.toLowerCase()
                )
            );

            setBusinessNews(business);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    const handleShow = (item) => {
        const id = item?._id || item?.id;
        const nextViews = getViews(item) + 1;

        if (id) {
            setBusinessNews(prev =>
                prev.map(n =>
                    (n._id === id || n.id === id)
                        ? { ...n, views: getViews(n) + 1 }
                        : n
                )
            );
        }

        setSelectedNews({ ...item, views: nextViews });
        setShow(true);
    };

    const handleClose = () => setShow(false);

    const getShareUrl = () => window.location.href;

    const openNewTab = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link copied!");
        } catch {
            window.prompt("Copy this link:", text);
        }
    };

    const shareTo = (platform, post) => {
        const url = getShareUrl();
        const title = post?.title || "News";

        switch (platform) {
            case "whatsapp":
                openNewTab(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`);
                break;
            case "facebook":
                openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
                break;
            case "x":
                openNewTab(`https://twitter.com/intent/tweet?text=${title}&url=${url}`);
                break;
            case "telegram":
                openNewTab(`https://t.me/share/url?url=${url}&text=${title}`);
                break;
            case "linkedin":
                openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
                break;
            case "email":
                openNewTab(`mailto:?subject=${title}&body=${url}`);
                break;
            case "copy":
                copyToClipboard(url);
                break;
            default:
                break;
        }
    };

    const ShareMenu = ({ post }) => (
        <Dropdown as={ButtonGroup}>
            <Button size="sm" className="btn-red">
                <i className="bi bi-share-fill me-1"></i> Share
            </Button>

            <Dropdown.Toggle split size="sm" className="btn-red" />

            <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => shareTo("whatsapp", post)}>WhatsApp</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("facebook", post)}>Facebook</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("x", post)}>X</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("telegram", post)}>Telegram</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("linkedin", post)}>LinkedIn</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => shareTo("email", post)}>Email</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("copy", post)}>Copy Link</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    return (
        <>
            <div className="business-hero text-white text-center py-5">
                <Container>
                    <h1 className="fw-bold display-5">Business News</h1>
                    <p className="lead">Latest updates on economy & market</p>
                </Container>
            </div>

            <Container className="py-5">
                <Row className="g-4">
                    {businessNews.map((news, i) => (
                        <Col md={4} sm={6} key={i}>
                            <Card className="card-hover h-100">
                                <div className="thumb-wrap">
                                    <Card.Img src={getImage(news)} className="thumb" />
                                </div>

                                <Card.Body>
                                    <div className="d-flex justify-content-between mb-2">
                                        <Badge bg="danger">{news.category}</Badge>

                                        <span className="news-views">
                                            <i className="bi bi-eye me-1"></i>
                                            {formatViews(getViews(news))}
                                        </span>
                                    </div>

                                    <Card.Title className="title-sm">
                                        {news.title}
                                    </Card.Title>

                                    <Card.Text className="text-muted">
                                        {news.shortDesc}
                                    </Card.Text>

                                    <div className="d-flex gap-2 flex-wrap">
                                        {/* ✅ ICON REMOVED */}
                                        <Button
                                            size="sm"
                                            className="btn-red"
                                            onClick={() => handleShow(news)}
                                        >
                                            Read More
                                        </Button>

                                        <ShareMenu post={news} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            <img
                                src={getImage(selectedNews)}
                                className="w-100 rounded mb-3"
                                alt=""
                            />

                            <p>{selectedNews.content}</p>

                            <div className="d-flex justify-content-between mt-3">
                                <span>
                                    <i className="bi bi-eye me-1"></i>
                                    {formatViews(getViews(selectedNews))}
                                </span>

                                <ShareMenu post={selectedNews} />
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <style>
                {`
                .business-hero {
                    background: linear-gradient(135deg, #d10000, #7a0000);
                }

                .btn-red {
                    background: #d10000;
                    border: none;
                    color: white;
                    font-weight: 700;
                }

                .btn-red:hover {
                    background: #a80000;
                }

                .card-hover {
                    border-radius: 14px;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.06);
                }

                .thumb-wrap {
                    overflow: hidden;
                    border-radius: 14px 14px 0 0;
                }

                .thumb {
                    height: 200px;
                    width: 100%;
                    object-fit: cover;
                }

                .news-views {
                    font-size: 13px;
                    color: #6c757d;
                    font-weight: 700;
                }

                .bi {
                    vertical-align: middle;
                }
                `}
            </style>
        </>
    );
};

export default Business;